import React, { useState, useEffect, useCallback, useMemo } from "react";
import CompanyLayout from "../../components/layouts/CompanyLayout";
import { auth, firestore, database, storage } from "../../firebaseConfig";
import { doc, getDoc, updateDoc, increment } from "firebase/firestore";
import { ref, onValue, push, set, runTransaction } from "firebase/database";
import { ref as storageRef, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import toast from "react-hot-toast";
import {
  FileText,
  FileSpreadsheet,
  Image,
  Settings,
  Upload,
  AlertCircle,
  Wallet,
  Hash,
  Loader2,
  CheckCircle,
  XCircle,
  Eye,
} from "lucide-react";

// Import tab components and constants
import { GeneratePapersTab, AnnotationQueueTab, ManagePapersTab } from "./components/milkJournals";
import {
  PAPER_TYPES,
  PAPER_COSTS,
  DEFAULT_FEE_PAYBILL,
  PAPER_ID_LENGTH,
  API_BASE_URL,
} from "./components/milkJournals/constants";
import { getStatusColor, fileToBase64, base64ToFile } from "./components/milkJournals/utils";

const MilkJournals = () => {
  const [activeTab, setActiveTab] = useState("generate");
  const [companyName, setCompanyName] = useState("");
  const [farmers, setFarmers] = useState([]);

  // Upload tab state
  const [userRole, setUserRole] = useState(null);
  const [companyBankBalance, setCompanyBankBalance] = useState(0);
  const [uploadPaperId, setUploadPaperId] = useState("");
  const [paperDetails, setPaperDetails] = useState(null);
  const [paperLookupLoading, setPaperLookupLoading] = useState(false);
  const [selectedImages, setSelectedImages] = useState([]);
  const [uploading, setUploading] = useState(false);

  // Image verification state
  const [verificationResults, setVerificationResults] = useState({}); // {index: {status, message, ...}}
  const [verifyingIndex, setVerifyingIndex] = useState(null);
  const [uploadProgress, setUploadProgress] = useState({}); // {index: percentage}
  const [imagePreviewUrls, setImagePreviewUrls] = useState([]); // Store blob URLs for cleanup

  const tabs = [
    { id: "generate", label: "Generate Papers", icon: <FileText size={20} /> },
    { id: "upload", label: "Upload Images", icon: <Image size={20} /> },
    { id: "annotate", label: "Annotation Queue", icon: <FileSpreadsheet size={20} /> },
    { id: "manage", label: "Manage Papers", icon: <Settings size={20} /> },
  ];

  // Fetch company name and user role
  useEffect(() => {
    const fetchCompanyNameAndRole = async () => {
      try {
        const user = auth.currentUser;
        if (!user) {
          return;
        }

        const userDoc = await getDoc(doc(firestore, "users", user.uid));
        if (userDoc.exists()) {
          const data = userDoc.data();
          const company = data.companyName || "";
          setCompanyName(company);

          // Check user role - first check if user is company admin
          if (data.accountType === "Company" || data.role === "Company") {
            setUserRole("CompanyAdmin");
          } else if (company) {
            // Prefer role from companies/{companyName}/employees/{uid}
            try {
              const employeeDoc = await getDoc(
                doc(firestore, "companies", company, "employees", user.uid)
              );
              if (employeeDoc.exists()) {
                const empData = employeeDoc.data();
                setUserRole(empData.role || empData.accountType || "Employee");
              } else {
                // fallback to user doc fields if employee record missing
                setUserRole(data.accountType || data.role || "Unknown");
              }
            } catch {
              // fallback on error
              setUserRole(data.accountType || data.role || "Unknown");
            }
          } else {
            // no company set; fall back to user doc role
            setUserRole(data.accountType || data.role || "Unknown");
          }
        }
      } catch (error) {
        console.error("Error fetching company name:", error);
        toast.error("Failed to load company information");
      }
    };

    fetchCompanyNameAndRole();
  }, []);

  // Fetch company bank balance
  useEffect(() => {
    if (!companyName || !database) return;

    const balanceRef = ref(database, `Companies/${companyName}/AccountBalances/company_bank`);
    const unsubscribe = onValue(balanceRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        setCompanyBankBalance(data.balance || 0);
      } else {
        setCompanyBankBalance(0);
      }
    });

    return unsubscribe;
  }, [companyName]);

  // Fetch farmers for the company
  useEffect(() => {
    if (!companyName || !database) return;

    const farmersRef = ref(database, `Companies/${companyName}/ValidatedFarmers`);

    const unsubscribeFarmers = onValue(farmersRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        const farmerList = Object.entries(data).map(([key, value]) => ({
          id: key,
          cloudName: value.CloudName || "Unnamed Farmer",
          otherName: value.OtherName || "N/A",
          address: value.Address || "N/A",
          phoneNumber: value.PhoneNumber || "N/A",
        }));
        setFarmers(farmerList);
      } else {
        setFarmers([]);
      }
    });

    return unsubscribeFarmers;
  }, [companyName]);

  // Cleanup blob URLs when selectedImages change or component unmounts
  useEffect(() => {
    // Create new preview URLs
    const urls = selectedImages.map((file) => URL.createObjectURL(file));
    setImagePreviewUrls(urls);

    // Cleanup function to revoke blob URLs
    return () => {
      urls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [selectedImages]);

  // Check if user can generate papers (CompanyAdmin or Manager only)
  const canGenerate = userRole === "CompanyAdmin" || userRole === "Manager";

  // Check if user can upload (CompanyAdmin or Manager only)
  const canUpload = userRole === "CompanyAdmin" || userRole === "Manager";

  // Look up paper details by ID
  const lookupPaper = async () => {
    if (!uploadPaperId.trim()) {
      toast.error("Please enter a paper ID");
      return;
    }

    setPaperLookupLoading(true);
    setPaperDetails(null);

    try {
      const paperDoc = await getDoc(doc(firestore, "milk_journals", uploadPaperId.trim()));

      if (paperDoc.exists()) {
        const data = paperDoc.data();
        setPaperDetails({
          ...data,
          id: uploadPaperId.trim(),
        });

        // Check if paper belongs to this company
        if (data.companyName !== companyName) {
          toast.error("This paper does not belong to your company");
          setPaperDetails(null);
        }
      } else {
        toast.error("Paper not found. Please check the ID.");
        setPaperDetails(null);
      }
    } catch (error) {
      console.error("Error looking up paper:", error);
      toast.error("Failed to lookup paper");
    } finally {
      setPaperLookupLoading(false);
    }
  };

  // Handle image selection
  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      // Validate file types
      const validFiles = files.filter((file) =>
        file.type.startsWith("image/")
      );
      if (validFiles.length !== files.length) {
        toast.error("Some files were not images and were skipped");
      }
      setSelectedImages(validFiles);
      // Reset verification results when new images are selected
      setVerificationResults({});
      setUploadProgress({});
    }
  };

  // Verify a single image via Django API
  const verifyImage = async (file, index) => {
    if (!paperDetails) {
      toast.error("Please lookup a paper ID first");
      return;
    }

    setVerifyingIndex(index);
    setVerificationResults((prev) => ({
      ...prev,
      [index]: { status: "verifying", message: "Verifying image..." },
    }));

    try {
      // Get auth token
      const user = auth.currentUser;
      if (!user) {
        throw new Error("User not authenticated");
      }
      const idToken = await user.getIdToken();

      // Convert file to base64
      const base64Image = await fileToBase64(file);

      // Call Django API for moderation
      const response = await fetch(`${API_BASE_URL}/api/moderate-image/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          image_base64: base64Image,
          expected_paper_id: paperDetails.id,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || "Verification failed");
      }

      // Update verification result
      const passed = result.is_safe && result.paper_id_verified;
      setVerificationResults((prev) => ({
        ...prev,
        [index]: {
          status: passed ? "passed" : "failed",
          message: result.message,
          is_safe: result.is_safe,
          paper_id_verified: result.paper_id_verified,
          safety_issues: result.safety_issues || [],
          mock: result.mock || false,
        },
      }));

      if (passed) {
        toast.success(`Image ${index + 1} verified successfully`);
      } else {
        toast.error(`Image ${index + 1}: ${result.message}`);
      }
    } catch (error) {
      console.error("Verification error:", error);
      setVerificationResults((prev) => ({
        ...prev,
        [index]: {
          status: "error",
          message: error.message || "Verification failed",
        },
      }));
      toast.error(`Failed to verify image ${index + 1}: ${error.message}`);
    } finally {
      setVerifyingIndex(null);
    }
  };

  // Verify all selected images
  const verifyAllImages = async () => {
    for (let i = 0; i < selectedImages.length; i++) {
      await verifyImage(selectedImages[i], i);
    }
  };

  // Upload a single image to Firebase Storage
  const uploadImageToStorage = async (file, index, isBlurred = false) => {
    if (!storage) {
      throw new Error("Firebase Storage not initialized");
    }

    const timestamp = Date.now();
    const safeName = file.name.replace(/\s+/g, "_");
    // Use different path for blurred images
    const basePath = isBlurred
      ? `milk-journals-blurred/${companyName}/${paperDetails.id}`
      : `milk-journals/${companyName}/${paperDetails.id}`;
    const path = `${basePath}/${timestamp}-${safeName}`;
    const sRef = storageRef(storage, path);
    const metadata = {
      contentType: file.type || "image/jpeg",
      customMetadata: {
        paperId: paperDetails.id,
        companyName: companyName,
        uploadedBy: auth.currentUser?.uid || "unknown",
        isBlurred: isBlurred ? "true" : "false",
      },
    };

    return new Promise((resolve, reject) => {
      const task = uploadBytesResumable(sRef, file, metadata);
      task.on(
        "state_changed",
        (snapshot) => {
          const pct = Math.round(
            (snapshot.bytesTransferred / snapshot.totalBytes) * 100
          );
          setUploadProgress((prev) => ({ ...prev, [index]: pct }));
        },
        (err) => reject(err),
        async () => {
          try {
            const url = await getDownloadURL(task.snapshot.ref);
            resolve({ url, storagePath: path });
          } catch (err) {
            reject(err);
          }
        }
      );
    });
  };

  // Process privacy blur for an image via backend API
  const processPrivacyBlurForUpload = async (file) => {
    try {
      // Get auth token
      const user = auth.currentUser;
      if (!user) {
        throw new Error("User not authenticated");
      }
      const idToken = await user.getIdToken();

      // Convert file to base64
      const base64Image = await fileToBase64(file);

      // Call Django API for privacy blur
      const response = await fetch(`${API_BASE_URL}/api/blur-privacy-info/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          image_base64: base64Image,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || "Failed to process privacy blur");
      }

      // Convert the blurred base64 image back to a File object
      const blurredFileName = `blurred_${file.name}`;
      const blurredFile = base64ToFile(result.blurred_image_base64, blurredFileName);

      return {
        success: true,
        blurredFile,
        message: result.message,
      };
    } catch (error) {
      console.error("Privacy blur processing error:", error);
      return {
        success: false,
        error: error.message,
      };
    }
  };

  // Check if all images have passed verification
  const allImagesPassed = useMemo(() => {
    if (selectedImages.length === 0) return false;
    return selectedImages.every((_, index) => {
      const result = verificationResults[index];
      return result && result.status === "passed";
    });
  }, [selectedImages, verificationResults]);

  // Count verified images
  const verifiedCount = useMemo(() => {
    return Object.values(verificationResults).filter(
      (r) => r.status === "passed"
    ).length;
  }, [verificationResults]);

  // Helper function to get the count of images for cost calculation
  const getImageCountForCost = useCallback(() => {
    const verifiedImageCount = Object.values(verificationResults).filter(
      (r) => r.status === "passed"
    ).length;
    return verifiedImageCount > 0 ? verifiedImageCount : selectedImages.length;
  }, [verificationResults, selectedImages.length]);

  // Calculate total cost for upload - only count verified images
  const calculateUploadCost = useCallback(() => {
    if (!paperDetails) return 0;
    const costPerPaper = PAPER_COSTS[paperDetails.paperType] || PAPER_COSTS[PAPER_TYPES.SINGLE_DAY];
    return costPerPaper * getImageCountForCost();
  }, [paperDetails, getImageCountForCost]);

  // Process upload with balance deduction and credit to default paybill
  const processUpload = async () => {
    if (!canUpload) {
      toast.error("Only company admin or manager can upload images");
      return;
    }

    if (!paperDetails) {
      toast.error("Please lookup and verify a paper ID first");
      return;
    }

    if (paperDetails.status === "closed") {
      toast.error("This paper is closed and cannot accept more uploads");
      return;
    }

    if (selectedImages.length === 0) {
      toast.error("Please select at least one image to upload");
      return;
    }

    // Check if all images have been verified
    if (!allImagesPassed) {
      toast.error("Please verify all images before uploading. Click 'Verify All' or verify each image individually.");
      return;
    }

    const totalCost = calculateUploadCost();

    if (companyBankBalance < totalCost) {
      toast.error(`Insufficient balance. Required: KES ${totalCost.toLocaleString()}, Available: KES ${companyBankBalance.toLocaleString()}`);
      return;
    }

    setUploading(true);

    // Track if images were successfully uploaded to determine whether to clear form
    let imagesUploadedSuccessfully = false;

    try {
      const now = new Date();
      const year = String(now.getFullYear());
      const month = String(now.getMonth() + 1).padStart(2, "0");
      const timestamp = now.getTime();

      // 1) Upload verified images to Firebase Storage (both original and blurred versions)
      const uploadedImages = [];
      const failedBlurImages = [];

      for (let i = 0; i < selectedImages.length; i++) {
        const result = verificationResults[i];
        if (result && result.status === "passed") {
          try {
            // Upload original image (for company use only)
            const originalUploadResult = await uploadImageToStorage(selectedImages[i], i, false);

            // Process privacy blur and upload blurred version (for Annotation Marketplace)
            const blurResult = await processPrivacyBlurForUpload(selectedImages[i]);

            if (!blurResult.success || !blurResult.blurredFile) {
              // Privacy blur failed - track this for user notification
              console.error(`Privacy blur failed for image ${i}:`, blurResult.error);
              failedBlurImages.push({
                index: i,
                fileName: selectedImages[i].name,
                error: blurResult.error || "Unknown blur processing error",
              });

              // Still store original upload for company use, but mark as blur failed
              uploadedImages.push({
                index: i,
                fileName: selectedImages[i].name,
                originalUrl: originalUploadResult.url,
                originalStoragePath: originalUploadResult.storagePath,
                blurredUrl: null,
                blurredStoragePath: null,
                blurFailed: true,
              });
              continue;
            }

            // Upload blurred version to separate path
            const blurredUploadResult = await uploadImageToStorage(blurResult.blurredFile, i, true);

            uploadedImages.push({
              index: i,
              fileName: selectedImages[i].name,
              // Original image URL - for company internal use
              originalUrl: originalUploadResult.url,
              originalStoragePath: originalUploadResult.storagePath,
              // Blurred image URL - for Annotation Marketplace (public)
              blurredUrl: blurredUploadResult.url,
              blurredStoragePath: blurredUploadResult.storagePath,
              blurFailed: false,
            });
          } catch (err) {
            console.error(`Failed to upload image ${i}:`, err);
            toast.error(`Failed to upload image ${i + 1}: ${err.message}`);
          }
        }
      }

      if (uploadedImages.length === 0) {
        toast.error("No images were uploaded successfully");
        setUploading(false);
        return;
      }

      // Mark that images were successfully uploaded to Storage
      imagesUploadedSuccessfully = true;

      // Check if any images failed blur processing
      const successfullyBlurredImages = uploadedImages.filter((img) => !img.blurFailed);

      if (successfullyBlurredImages.length === 0) {
        // All images failed blur processing - cannot proceed with annotation queue
        toast.error("Privacy blur processing failed for all images. Images were uploaded but will not be available for annotation.");
        // Still continue to record the upload for company records, but warn user
      } else if (failedBlurImages.length > 0) {
        // Some images failed blur processing
        toast.warning(`${failedBlurImages.length} image(s) failed privacy blur processing and will not be available for annotation.`);
      }

      // 2) Deduct from company bank balance using transaction with balance validation inside
      const actualCost = PAPER_COSTS[paperDetails.paperType] * uploadedImages.length;
      const companyBankRef = ref(database, `Companies/${companyName}/AccountBalances/company_bank`);
      let transactionSucceeded = false;
      await runTransaction(companyBankRef, (current) => {
        const curBal = current === null ? 0 : Number(current.balance || 0);

        // Validate balance inside transaction to prevent race conditions
        if (curBal < actualCost) {
          // Return undefined to abort the transaction
          return undefined;
        }

        transactionSucceeded = true;
        return { balance: curBal - actualCost, updatedAt: timestamp };
      });

      if (!transactionSucceeded) {
        toast.error(`Transaction failed: Insufficient balance. Please try again.`);
        setUploading(false);
        return;
      }

      // 3) Credit to DEFAULT_ACC_001 paybill (for annotation fees)
      const defaultPaybillRef = ref(database, `Companies/${DEFAULT_FEE_PAYBILL}/AccountBalances/company_bank`);
      await runTransaction(defaultPaybillRef, (current) => {
        if (current === null) {
          return { balance: actualCost, updatedAt: timestamp };
        }
        const curBal = Number(current.balance || 0);
        return { balance: curBal + actualCost, updatedAt: timestamp };
      });

      // 4) Record the transaction in company accounts
      const txId = push(ref(database)).key;
      const txData = {
        id: txId,
        date: now.toISOString(),
        createdAt: timestamp,
        amount: actualCost,
        direction: "debit",
        type: "milk_journal_upload_fee",
        notes: `Upload fee for paper ${paperDetails.id} - ${uploadedImages.length} images`,
        reference: paperDetails.id,
        paperType: paperDetails.paperType,
        imageCount: uploadedImages.length,
      };

      const companyTxPath = `Companies/${companyName}/Accounts/company_bank/${year}/${month}/${txId}`;
      await set(ref(database, companyTxPath), txData);

      // 5) Record credit transaction for DEFAULT_ACC_001
      const creditTxId = push(ref(database)).key;
      const creditTxData = {
        id: creditTxId,
        date: now.toISOString(),
        createdAt: timestamp,
        amount: actualCost,
        direction: "credit",
        type: "milk_journal_upload_fee",
        notes: `Upload fee from ${companyName} for paper ${paperDetails.id}`,
        reference: paperDetails.id,
        sourceCompany: companyName,
        paperType: paperDetails.paperType,
        imageCount: uploadedImages.length,
      };

      const defaultTxPath = `Companies/${DEFAULT_FEE_PAYBILL}/Accounts/company_bank/${year}/${month}/${creditTxId}`;
      await set(ref(database, defaultTxPath), creditTxData);

      // 6) Update paper status to "working" and record upload
      await updateDoc(doc(firestore, "milk_journals", paperDetails.id), {
        status: paperDetails.status === "active" ? "working" : paperDetails.status,
        lastUploadAt: now.toISOString(),
        uploadCount: increment(uploadedImages.length),
        totalFeePaid: increment(actualCost),
      });

      // 7) Store upload record with actual image URLs (original for company use)
      const uploadId = push(ref(database)).key;
      const uploadRecord = {
        id: uploadId,
        paperId: paperDetails.id,
        companyName,
        uploadedBy: auth.currentUser?.uid,
        uploadedAt: now.toISOString(),
        imageCount: uploadedImages.length,
        feePaid: actualCost,
        paperType: paperDetails.paperType,
        status: "pending_annotation", // Ready for annotation after verification passed
        // Store both original and blurred URLs for company records
        images: uploadedImages.map((img) => ({
          fileName: img.fileName,
          originalUrl: img.originalUrl,
          originalStoragePath: img.originalStoragePath,
          blurredUrl: img.blurredUrl,
          blurredStoragePath: img.blurredStoragePath,
        })),
      };

      await set(
        ref(database, `Companies/${companyName}/MilkJournalUploads/${paperDetails.id}/${uploadId}`),
        uploadRecord
      );

      // 8) Create global snapshot for Annotation Marketplace visibility
      // This allows Farmers, Clients, and Company Employees to trace the paper
      // IMPORTANT: Only expose blurred URLs to protect sensitive information
      // Only proceed if there are successfully blurred images
      const blurredImages = uploadedImages
        .filter((img) => !img.blurFailed && img.blurredUrl)
        .map((img) => ({
          fileName: img.fileName,
          url: img.blurredUrl,
          storagePath: img.blurredStoragePath,
          // Include the original storage path so the blurred image can be traced to the original company image.
          // Note: This is a storage path string (not a public URL). Access to the original image is still governed by Storage rules.
          originalStoragePath: img.originalStoragePath,
        }));

      if (blurredImages.length > 0) {
        try {
          const globalSnapshotData = {
            paperId: paperDetails.id,
            companyName,
            paperType: paperDetails.paperType,
            dateRange: paperDetails.dateRange,
            totalFarmers: paperDetails.totalFarmers,
            totalPages: paperDetails.totalPages,
            uploadedAt: now.toISOString(),
            uploadedBy: auth.currentUser?.uid,
            status: "pending_annotation",
            imageCount: blurredImages.length,
            // Firestore reference path for paper metadata
            firestoreRef: `milk_journals/${paperDetails.id}`,
            // Reference to the company upload record created above so you can trace the blurred images to the company upload
            originalUploadRef: `Companies/${companyName}/MilkJournalUploads/${paperDetails.id}/${uploadId}`,
            // Storage bucket URLs for viewing images in Annotation Marketplace
            // SECURITY: Only expose blurred URLs to protect company name and farmer names
            images: blurredImages,
            // Progress tracking
            openedCount: 0,       // Number of users who have opened this paper
            completedCount: 0,    // Number of users who have completed annotation
            // List of users who opened/completed (for detailed tracking)
            openedBy: {},
            completedBy: {},
          };

          await set(
            ref(database, `GlobalAnnotationQueue/${paperDetails.id}`),
            globalSnapshotData
          );

          console.log(`Successfully added paper ${paperDetails.id} to GlobalAnnotationQueue with ${blurredImages.length} images`);
          toast.success(`Upload complete! KES ${actualCost.toLocaleString()} deducted. ${blurredImages.length} image(s) uploaded and queued for annotation.`);
        } catch (queueError) {
          // Log the error but don't fail the entire upload
          console.error("Failed to add paper to GlobalAnnotationQueue:", queueError);
          toast.warning("Images uploaded successfully, but failed to add to annotation marketplace. Please contact support.");
        }
      } else {
        // No blurred images available - do not add to GlobalAnnotationQueue
        toast.success(`Upload complete! KES ${actualCost.toLocaleString()} deducted. Images saved for company records but privacy blur failed - not available for public annotation.`);
      }

    } catch (error) {
      console.error("Error processing upload:", error);
      toast.error("Failed to process upload. Please try again.");
    } finally {
      setUploading(false);

      // Clear form only if images were successfully uploaded to Storage
      // This prevents re-upload of the same images even if subsequent operations failed
      // (e.g., GlobalAnnotationQueue update, payment processing, or database writes)
      if (imagesUploadedSuccessfully) {
        setSelectedImages([]);
        setVerificationResults({});
        setUploadProgress({});
        setUploadPaperId("");  // Clear paper ID input
        setPaperDetails(null);  // Clear paper details to force new lookup
        console.log("Upload form cleared successfully after image upload");
      }
    }
  };

  return (
    <CompanyLayout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-cyan-700 flex items-center gap-2">
              <FileText size={32} />
              Milk Journal Annotation
            </h1>
            <p className="text-gray-600 mt-2">
              Generate and manage milk journal papers for image annotation tasks
            </p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex space-x-4 border-b border-gray-200">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`pb-2 px-4 font-medium transition-all duration-200 flex items-center gap-2 ${
                activeTab === tab.id
                  ? "text-cyan-700 border-b-2 border-cyan-700"
                  : "text-gray-500 hover:text-cyan-600"
              }`}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {/* Generate Papers Tab */}
        {activeTab === "generate" && (
          <GeneratePapersTab
            companyName={companyName}
            farmers={farmers}
            canGenerate={canGenerate}
            userRole={userRole}
          />
        )}

        {/* Upload Tab */}
        {activeTab === "upload" && (
          <div className="space-y-6">
            {/* Access Control Check */}
            {!canUpload ? (
              <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
                <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-red-700 mb-2">Access Restricted</h3>
                <p className="text-red-600">
                  Only Company Admins and Managers can upload milk journal images.
                </p>
                <p className="text-sm text-red-500 mt-2">
                  Your current role: {userRole || "Unknown"}
                </p>
              </div>
            ) : (
              <>
                {/* Company Bank Balance Display */}
                <div className="bg-white rounded-lg shadow p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-cyan-100 flex items-center justify-center">
                        <Wallet className="text-cyan-600" size={24} />
                      </div>
                      <div>
                        <h3 className="text-sm font-medium text-gray-600">Company Bank Balance</h3>
                        <p className="text-2xl font-bold text-cyan-700">
                          KES {companyBankBalance.toLocaleString()}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-gray-500">Upload Costs:</p>
                      <p className="text-xs text-gray-600">Single Day: KES {PAPER_COSTS[PAPER_TYPES.SINGLE_DAY]}/image</p>
                      <p className="text-xs text-gray-600">Weekly: KES {PAPER_COSTS[PAPER_TYPES.WEEKLY]}/image</p>
                      <p className="text-xs text-gray-600">Half Monthly: KES {PAPER_COSTS[PAPER_TYPES.HALF_MONTHLY]}/image</p>
                    </div>
                  </div>
                </div>

                {/* Paper ID Lookup */}
                <div className="bg-white rounded-lg shadow p-6">
                  <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
                    <Hash size={24} />
                    Enter Paper ID
                  </h2>
                  <div className="flex gap-4">
                    <input
                      type="text"
                      placeholder={`Enter the ${PAPER_ID_LENGTH}-digit paper ID...`}
                      value={uploadPaperId}
                      onChange={(e) => setUploadPaperId(e.target.value)}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500"
                      maxLength={PAPER_ID_LENGTH}
                    />
                    <button
                      onClick={lookupPaper}
                      disabled={paperLookupLoading || !uploadPaperId.trim()}
                      className={`px-6 py-2 rounded-lg transition flex items-center gap-2 ${
                        paperLookupLoading || !uploadPaperId.trim()
                          ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                          : "bg-cyan-600 text-white hover:bg-cyan-700"
                      }`}
                    >
                      {paperLookupLoading ? (
                        <>
                          <Loader2 className="animate-spin" size={18} />
                          Looking up...
                        </>
                      ) : (
                        "Verify Paper"
                      )}
                    </button>
                  </div>
                </div>

                {/* Paper Details */}
                {paperDetails && (
                  <div className="bg-white rounded-lg shadow p-6">
                    <h2 className="text-xl font-semibold text-gray-800 mb-4">Paper Details</h2>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <p className="text-sm text-gray-600">Paper ID</p>
                        <p className="font-semibold">{paperDetails.id}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Paper Type</p>
                        <p className="font-semibold capitalize">
                          {paperDetails.paperType?.replace("_", " ")}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Date Range</p>
                        <p className="font-semibold">{paperDetails.dateRange}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Status</p>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(paperDetails.status)}`}>
                          {paperDetails.status?.toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Total Farmers</p>
                        <p className="font-semibold">{paperDetails.totalFarmers}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Total Pages</p>
                        <p className="font-semibold">{paperDetails.totalPages}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Created At</p>
                        <p className="font-semibold text-sm">
                          {new Date(paperDetails.createdAt).toLocaleDateString()}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Upload Cost/Image</p>
                        <p className="font-semibold text-cyan-700">
                          KES {PAPER_COSTS[paperDetails.paperType] || PAPER_COSTS[PAPER_TYPES.SINGLE_DAY]}
                        </p>
                      </div>
                    </div>

                    {paperDetails.status === "closed" && (
                      <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                        <p className="text-sm text-red-700 flex items-center gap-2">
                          <AlertCircle size={16} />
                          This paper is closed and cannot accept more uploads.
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Image Upload Section */}
                {paperDetails && paperDetails.status !== "closed" && (
                  <div className="bg-white rounded-lg shadow p-6">
                    <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
                      <Upload size={24} />
                      Upload Images
                    </h2>

                    {/* File Input */}
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-cyan-500 transition">
                      <input
                        type="file"
                        id="imageUpload"
                        accept="image/*"
                        multiple
                        onChange={handleImageSelect}
                        className="hidden"
                      />
                      <label
                        htmlFor="imageUpload"
                        className="cursor-pointer"
                      >
                        <Image className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                        <p className="text-gray-600 mb-2">
                          Click to select images or drag and drop
                        </p>
                        <p className="text-sm text-gray-400">
                          Supported formats: JPG, PNG, WEBP
                        </p>
                      </label>
                    </div>

                    {/* Selected Images with Verification Status */}
                    {selectedImages.length > 0 && (
                      <div className="mt-4">
                        <div className="flex justify-between items-center mb-3">
                          <p className="text-sm font-medium text-gray-700">
                            Selected: {selectedImages.length} image(s) • Verified: {verifiedCount}
                          </p>
                          <button
                            onClick={verifyAllImages}
                            disabled={verifyingIndex !== null || selectedImages.length === 0}
                            className={`px-4 py-2 rounded-lg text-sm transition flex items-center gap-2 ${
                              verifyingIndex !== null
                                ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                                : "bg-blue-600 text-white hover:bg-blue-700"
                            }`}
                          >
                            {verifyingIndex !== null ? (
                              <>
                                <Loader2 className="animate-spin" size={16} />
                                Verifying...
                              </>
                            ) : (
                              <>
                                <Eye size={16} />
                                Verify All Images
                              </>
                            )}
                          </button>
                        </div>

                        {/* Image Cards with Verification Status */}
                        <div className="space-y-3 max-h-96 overflow-y-auto">
                          {selectedImages.map((file, idx) => {
                            const result = verificationResults[idx];
                            const progress = uploadProgress[idx];

                            return (
                              <div
                                key={idx}
                                className={`p-4 border rounded-lg flex items-center gap-4 ${
                                  result?.status === "passed"
                                    ? "border-green-300 bg-green-50"
                                    : result?.status === "failed"
                                    ? "border-red-300 bg-red-50"
                                    : result?.status === "error"
                                    ? "border-orange-300 bg-orange-50"
                                    : "border-gray-200 bg-gray-50"
                                }`}
                              >
                                {/* Image Preview */}
                                <div className="w-16 h-16 bg-gray-200 rounded overflow-hidden flex-shrink-0">
                                  <img
                                    src={imagePreviewUrls[idx] || ""}
                                    alt={file.name}
                                    className="w-full h-full object-cover"
                                  />
                                </div>

                                {/* File Info */}
                                <div className="flex-1 min-w-0">
                                  <p className="font-medium text-gray-800 truncate">
                                    {file.name}
                                  </p>
                                  <p className="text-sm text-gray-500">
                                    {(file.size / 1024).toFixed(1)} KB
                                  </p>

                                  {/* Verification Status Message */}
                                  {result && (
                                    <p className={`text-sm mt-1 ${
                                      result.status === "passed" ? "text-green-700" :
                                      result.status === "failed" ? "text-red-700" :
                                      result.status === "verifying" ? "text-blue-700" :
                                      "text-orange-700"
                                    }`}>
                                      {result.message}
                                      {result.mock && <span className="text-gray-500"> (Mock)</span>}
                                    </p>
                                  )}

                                  {/* Upload Progress Bar */}
                                  {progress !== undefined && progress < 100 && (
                                    <div className="mt-2">
                                      <div className="h-2 bg-gray-200 rounded overflow-hidden">
                                        <div
                                          className="h-full bg-cyan-500 transition-all duration-300"
                                          style={{ width: `${progress}%` }}
                                        />
                                      </div>
                                      <p className="text-xs text-gray-500 mt-1">
                                        Uploading: {progress}%
                                      </p>
                                    </div>
                                  )}
                                </div>

                                {/* Status Icon / Verify Button */}
                                <div className="flex-shrink-0">
                                  {result?.status === "passed" ? (
                                    <CheckCircle className="text-green-600" size={24} />
                                  ) : result?.status === "failed" ? (
                                    <XCircle className="text-red-600" size={24} />
                                  ) : result?.status === "verifying" ? (
                                    <Loader2 className="animate-spin text-blue-600" size={24} />
                                  ) : result?.status === "error" ? (
                                    <AlertCircle className="text-orange-600" size={24} />
                                  ) : (
                                    <button
                                      onClick={() => verifyImage(file, idx)}
                                      disabled={verifyingIndex !== null}
                                      className={`px-3 py-1 rounded text-sm ${
                                        verifyingIndex !== null
                                          ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                                          : "bg-blue-100 text-blue-700 hover:bg-blue-200"
                                      }`}
                                    >
                                      Verify
                                    </button>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>

                        {/* Verification Summary */}
                        {Object.keys(verificationResults).length > 0 && (
                          <div className={`mt-4 p-3 rounded-lg ${
                            allImagesPassed
                              ? "bg-green-50 border border-green-200"
                              : "bg-amber-50 border border-amber-200"
                          }`}>
                            <p className={`text-sm ${allImagesPassed ? "text-green-800" : "text-amber-800"}`}>
                              {allImagesPassed ? (
                                <>
                                  <CheckCircle className="inline mr-2" size={16} />
                                  All images verified! Ready to upload.
                                </>
                              ) : (
                                <>
                                  <AlertCircle className="inline mr-2" size={16} />
                                  {verifiedCount} of {selectedImages.length} images verified.
                                  Please verify all images before uploading.
                                </>
                              )}
                            </p>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Cost Summary */}
                    {selectedImages.length > 0 && (
                      <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                        <div className="flex justify-between items-center">
                          <div>
                            <p className="text-sm text-amber-800">Total Upload Cost:</p>
                            <p className="text-2xl font-bold text-amber-900">
                              KES {calculateUploadCost().toLocaleString()}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm text-amber-700">
                              {getImageCountForCost()} × KES {PAPER_COSTS[paperDetails.paperType] || PAPER_COSTS[PAPER_TYPES.SINGLE_DAY]}
                            </p>
                            {companyBankBalance < calculateUploadCost() && (
                              <p className="text-sm text-red-600 font-medium">
                                ⚠️ Insufficient balance!
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Upload Button */}
                    <div className="mt-6 flex justify-end gap-3">
                      {selectedImages.length > 0 && !allImagesPassed && (
                        <button
                          onClick={() => {
                            setSelectedImages([]);
                            setVerificationResults({});
                            setUploadProgress({});
                          }}
                          className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
                        >
                          Clear All
                        </button>
                      )}
                      <button
                        onClick={processUpload}
                        disabled={
                          uploading ||
                          selectedImages.length === 0 ||
                          !allImagesPassed ||
                          companyBankBalance < calculateUploadCost()
                        }
                        className={`px-6 py-3 rounded-lg transition flex items-center gap-2 ${
                          uploading ||
                          selectedImages.length === 0 ||
                          !allImagesPassed ||
                          companyBankBalance < calculateUploadCost()
                            ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                            : "bg-cyan-600 text-white hover:bg-cyan-700"
                        }`}
                      >
                        {uploading ? (
                          <>
                            <Loader2 className="animate-spin" size={18} />
                            Uploading...
                          </>
                        ) : (
                          <>
                            <Upload size={18} />
                            Upload & Pay KES {calculateUploadCost().toLocaleString()}
                          </>
                        )}
                      </button>
                    </div>

                    {/* Content Moderation Notice */}
                    <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <p className="text-sm text-blue-800">
                        <strong>📋 Verification Process:</strong>
                      </p>
                      <ul className="text-sm text-blue-700 mt-2 space-y-1 list-disc list-inside">
                        <li>Paper ID Verification: OCR checks that the paper ID in the image matches</li>
                        <li>Content Safety: Images are scanned for inappropriate content</li>
                        <li>Only verified images can be uploaded to Firebase Storage</li>
                        <li>Payment is processed after successful upload</li>
                      </ul>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Annotate Tab - Annotation Queue */}
        {activeTab === "annotate" && (
          <AnnotationQueueTab companyName={companyName} userRole={userRole} />
        )}

        {/* Manage Tab - Placeholder */}
        {activeTab === "manage" && <ManagePapersTab />}
      </div>
    </CompanyLayout>
  );
};

export default MilkJournals;
